import { Roles, type AppUser, type User } from '@/lib/types'

/**
 * Wraps the raw `User` model with role/permission helpers in two
 * scopes:
 *
 *   - **Current tenant** (`can`, `hasPermissions`, `hasRoles`) —
 *     reads `permission_list` / `role_list`. Primary call sites:
 *     route guards (`hasRoles(meta.roles)`), menu filtering,
 *     per-action gating in views.
 *   - **Across teams** (`canAnywhere`, `hasPermissionsAnywhere`,
 *     `hasRoleAnywhere`) — reads `permission_list_across_teams` /
 *     `role_list_across_teams`. Primary call site: platform-tier
 *     detection (`hasRoleAnywhere([Roles.SUPER_ADMIN])`).
 *
 * **Platform-tier bypass is INSIDE the helpers** — every permission
 * / role check short-circuits to `true` when the user is
 * `super_admin` or `system` (across-teams). This mirrors the
 * server's `BasePolicyAbs::before()` shortcut so that menus,
 * route guards, action buttons, and any future call site
 * uniformly inherit the bypass without each consumer needing to
 * know about it.
 *
 * Lives in its own module (separate from `useAuth.ts`) to break a
 * circular import: `useGlobalStore` consumes `buildUser` at module
 * init while `useAuth` consumes `useGlobalStore`.
 */
const PLATFORM_TIER_ROLES = [Roles.SUPER_ADMIN, Roles.SYSTEM] as const

const isPlatformTier = (user?: User): boolean => {
  const roles = user?.role_list_across_teams
  if (!roles || roles.length === 0) return false
  return roles.some((r) => PLATFORM_TIER_ROLES.includes(r as (typeof PLATFORM_TIER_ROLES)[number]))
}

export const buildUser = (user?: User): AppUser => ({
  detail: user ?? null,

  // Current-tenant scope — platform-tier bypasses every check.
  can(permission: string): boolean {
    if (isPlatformTier(user)) return true
    return user?.permission_list?.includes(permission) ?? false
  },
  hasPermissions(permissions: string[]): boolean {
    if (!permissions.length) return true
    if (isPlatformTier(user)) return true
    return permissions.every((p) => user?.permission_list?.includes(p))
  },
  hasRoles(roles: string[]): boolean {
    if (!roles.length) return true
    return roles.some((r) => user?.role_list?.includes(r))
  },

  // Across-teams scope. `hasRoleAnywhere` is the canonical
  // platform-tier probe; we DON'T self-bypass it (that'd be
  // tautological — the function would always return true).
  // `canAnywhere` / `hasPermissionsAnywhere` follow the same
  // bypass rule as their tenant-scoped cousins.
  canAnywhere(permission: string): boolean {
    if (isPlatformTier(user)) return true
    return user?.permission_list_across_teams?.includes(permission) ?? false
  },
  hasPermissionsAnywhere(permissions: string[]): boolean {
    if (!permissions.length) return true
    if (isPlatformTier(user)) return true
    return permissions.every((p) => user?.permission_list_across_teams?.includes(p))
  },
  hasRoleAnywhere(roles: string[]): boolean {
    if (!roles.length) return true
    return roles.some((r) => user?.role_list_across_teams?.includes(r))
  },
})
